import { useQueryClient } from '@tanstack/react-query'
import { useState, type MouseEvent as ReactMouseEvent } from 'react'
import { unassignedTallyQuery, type UnassignedTally } from '../api/inventoree'
import { Modal } from '../ui/Modal'
import { UnassignedCard } from './UnassignedCard'
import './UnassignedFirst.css'

/**
 * NEALOCATELE PRIMELE, IN LISTELE INVENTOREE.
 *
 * Fixarea se face pe server, o singura data, in `UnassignedPin` (cheia de
 * sortare pusa INAINTEA celei alese de om). Aici e numai ce se vede:
 *
 *  - `unassignedRowClass` - randul fixat capata o clasa, ca omul sa inteleaga
 *    de ce e sus. Se pune din coloana 0 (deschizatorul / bifa), fiindca ea nu
 *    se poate ascunde din meniul "Columns": o insigna pusa pe coloana de
 *    proprietar ar dispare cu ea.
 *  - `UnassignedCount` - insigna "N unassigned" din antetul tabelului, care
 *    duce la lista Unknown.
 *  - `UnassignedAssignBar` - butonul Assign din randul desfacut, cu fisa care
 *    exista deja (`UnassignedCard`, `GET /api/v2/inventoree/unassigned/{id}/card`)
 *    intr-o fereastra. Fisa NU se rescrie aici.
 *  - `assignCellHtml` + `useAssignCell` - acelasi buton, dar IN CELULA de
 *    proprietar, unde pana pe 24 septembrie 2026 scria textul rosu
 *    "Unassigned". Deschide ACEEASI fereastra (`AssignModal`), nu o a doua
 *    cale.
 *
 * Dreptul e CEL AL PAGINII UNKNOWN, nu unul nou: `canAssign` din
 * `/inventoree/unassigned/tally` e chiar
 * `overview.unassigned_details_assign.read`, dreptul pe care il cere
 * `UnassignedCard` pentru coloana Assign. Fara el nu se deseneaza butonul, deci
 * nici fereastra - si chiar daca ar ajunge cineva la ea, capatul raspunde 403.
 */

/** Numele coloanei pe care o trimite serverul (UnassignedPin::ROW_FLAG). */
const FLAG = 'is_unassigned'

/**
 * Randul e al unui aparat nealocat? Coloana vine din SQL, deci poate sosi si
 * ca 1, si ca "1" - de-aia `Number`, nu adevarul JavaScript al sirului.
 */
export function isUnassignedRow(row: Record<string, unknown>): boolean {
  const flag = row[FLAG]
  return flag === true || Number(flag) === 1
}

export const UNASSIGNED_ROW_CLASS = 'od-unassigned-row'

/** `rowClass` de pus pe coloana 0 a listelor care fixeaza nealocatele. */
export function unassignedRowClass(row: Record<string, unknown>): string | undefined {
  return isUnassignedRow(row) ? UNASSIGNED_ROW_CLASS : undefined
}

/**
 * "3 unassigned" in antetul tabelului. Lipseste cu totul cand nu e niciunul sau
 * cand omul n-are drept pe lista Unknown - o cifra care duce intr-un 403 e mai
 * rea decat nicio cifra.
 *
 * `/app` in fata: routerul sta sub `basepath: '/app'`, iar un `<a href>` scris
 * fara el ocoleste routerul si pleaca la portalul vechi, care nu mai sta pe
 * server. Vezi `ui/rawLinks.test.ts`.
 */
export function UnassignedCount({ tally }: { tally: UnassignedTally | undefined }) {
  if (!tally || !tally.canList || tally.count < 1) return null

  return (
    <a className="btn btn-default od-unassigned-count tk-header-extra" href="/app/inventoree/unassigned" title="Devices with nobody in charge of them">
      <i className="fas fa-user-slash" aria-hidden="true"></i> {tally.count} unassigned
    </a>
  )
}

/**
 * FEREASTRA DE ALOCARE, SCRISA O SINGURA DATA.
 *
 * Fisa care exista deja (`UnassignedCard`) intr-un `Modal` cu `portal`. O
 * folosesc amandoua caile - banda randului desfacut si butonul din celula de
 * proprietar - ca sa nu existe doua ferestre cu doua purtari.
 */
export function AssignModal({
  domId,
  id,
  open,
  onClose,
  onAssigned,
}: {
  domId: string
  id: number
  open: boolean
  onClose(): void
  onAssigned(): void
}) {
  return (
    <Modal
      id={domId}
      open={open}
      onClose={onClose}
      title={
        <>
          <i className="fas fa-user-check" aria-hidden="true"></i> Assign device
        </>
      }
      large
      /* Randul desfacut e un context de stacking al lui: fara portal,
         fereastra ar ramane sub valul gri. Vezi `Modal`. */
      portal
    >
      {/* `inModal`: bara cu Add to CMDB ramane lipita de capatul de jos
          al ferestrei, si roata mouse-ului deruleaza fisa, nu pagina din
          spate. In pagina intreaga fisa ramane cum e. */}
      {open ? <UnassignedCard id={id} onAssigned={onAssigned} inModal /> : null}
    </Modal>
  )
}

/**
 * Clasa butonului Assign din CELULA de proprietar. Pe ea sta delegarea, fiindca
 * celulele listelor sunt SIRURI de HTML date lui `DataTable`, nu JSX: un
 * `onClick` de React n-are unde sa se lege. Acelasi tipar ca la
 * `.moveToDepository` / `.remove-device` (`ListWrites.useRowActions`,
 * `UnassignedPage.onTableClick`): clasa plus `data-id`, si un singur ascultator
 * pe tabel.
 */
export const ASSIGN_CELL_CLASS = 'od-assign-cell'

/**
 * Butonul din celula de proprietar. Textul si iconita sunt cele ale butonului
 * din randul desfacut ("Assign", `fa-user-check`), iar `title` e chiar
 * lamurirea din banda - nimic nou de citit pentru om.
 *
 * Masura si culorile stau in `UnassignedFirst.css`, nu aici.
 */
export function assignCellHtml(deviceId: number): string {
  return (
    `<button type="button" class="btn btn-xs ${ASSIGN_CELL_CLASS}" data-id="${deviceId}"` +
    ' title="Nobody is in charge of this device yet.">' +
    '<i class="fas fa-user-check" aria-hidden="true"></i> Assign</button>'
  )
}

/**
 * Ascultatorul butoanelor Assign din celule, plus fereastra pe care o deschid.
 *
 * `canAssign` se intoarce de aici ca ecranul sa-l dea coloanelor: fara dreptul
 * de alocare butonul nu se DESENEAZA deloc, si in celula ramane textul de pana
 * acum. Un buton care raspunde 403 e mai rau decat textul.
 *
 * `onAssignCellClick` spune daca apasarea a fost a lui, ca ecranul sa nu mai
 * caute dupa el.
 */
export function useAssignCell(tally: UnassignedTally | undefined, onAssigned: () => void) {
  /* Aparatul ramane ales si dupa inchidere, cat tine stingerea ferestrei
     (300 ms in `Modal`); `open` e cel care deschide si inchide. */
  const [id, setId] = useState<number | null>(null)
  const [open, setOpen] = useState(false)
  const queries = useQueryClient()
  const canAssign = tally?.canAssign === true

  const onAssignCellClick = (event: ReactMouseEvent<HTMLElement>): boolean => {
    /* `closest` fiindca apasarea nimereste de cele mai multe ori iconita
       dinauntru, nu butonul. */
    const button = (event.target as HTMLElement).closest(`.${ASSIGN_CELL_CLASS}`)
    if (!(button instanceof HTMLElement)) return false
    event.preventDefault()
    const deviceId = Number(button.dataset.id ?? 0)
    if (deviceId < 1) return true
    setId(deviceId)
    setOpen(true)
    return true
  }

  const done = () => {
    setOpen(false)
    void queries.invalidateQueries({ queryKey: unassignedTallyQuery.queryKey })
    onAssigned()
  }

  const modal =
    canAssign && id !== null ? (
      <AssignModal domId={`odAssignCellModal-${id}`} id={id} open={open} onClose={() => setOpen(false)} onAssigned={done} />
    ) : null

  return { canAssign, onAssignCellClick, modal }
}

/**
 * Banda de deasupra fisei, in randul desfacut al unui aparat nealocat. Dupa
 * alocare fereastra se inchide, lista se cere din nou - randul RAMANE in ea,
 * doar isi pierde fixarea - si insigna din antet scade.
 */
export function UnassignedAssignBar({
  id,
  row,
  tally,
  onAssigned,
}: {
  id: number
  row: Record<string, unknown>
  tally: UnassignedTally | undefined
  onAssigned(): void
}) {
  const [open, setOpen] = useState(false)
  const queries = useQueryClient()
  if (!isUnassignedRow(row)) return null

  const done = () => {
    setOpen(false)
    void queries.invalidateQueries({ queryKey: unassignedTallyQuery.queryKey })
    onAssigned()
  }

  return (
    <div className="od-unassigned-bar">
      <span className="od-unassigned-mark">
        <i className="fas fa-user-slash" aria-hidden="true"></i> Unassigned
      </span>
      <span className="od-unassigned-hint">Nobody is in charge of this device yet.</span>
      {tally?.canAssign ? (
        <button type="button" className="btn btn-sm btn-theme od-unassigned-assign" onClick={() => setOpen(true)}>
          <i className="fas fa-user-check" aria-hidden="true"></i> Assign
        </button>
      ) : null}
      {tally?.canAssign ? (
        <AssignModal domId={`odAssignModal-${id}`} id={id} open={open} onClose={() => setOpen(false)} onAssigned={done} />
      ) : null}
    </div>
  )
}
